import type { FaqEntry } from '@/content/faq';

/**
 * Páginas por setor (`/setores/:slug`).
 *
 * Regra de conteúdo: cada setor só cita apps que existem no catálogo público
 * (`apps` são slugs de `/api/public/apps`). Se um app sair do catálogo, ele
 * some da página sozinho no próximo build, e a lista nunca promete o que o
 * produto não tem.
 */
export interface Sector {
  slug: string;
  /** Nome curto, usado em menu e breadcrumb. */
  name: string;
  /** Termo de busca que a página responde. Vai no título. */
  searchTerm: string;
  headline: string;
  lead: string;
  /** O dia a dia do setor no WhatsApp, em parágrafos. */
  context: string[];
  /** O que muda com a Operax, cada item ligado a um app quando houver. */
  flows: { title: string; text: string; app?: string }[];
  apps: string[];
  faq: FaqEntry[];
}

export const sectors: Sector[] = [
  {
    slug: 'clinicas',
    name: 'Clínicas e saúde',
    searchTerm: 'Sistema para clínica com WhatsApp',
    headline: 'Clínica que agenda, confirma e atende pelo WhatsApp oficial.',
    lead: 'Paciente marca pelo WhatsApp, recebe a confirmação, chega na recepção e sai com a receita. Tudo no mesmo sistema, com prontuário e financeiro do atendimento.',
    context: [
      'Em clínica, o WhatsApp virou a recepção de verdade. É por ali que o paciente pergunta horário, remarca, manda exame e pede segunda via. Quando isso fica num celular da recepção, a agenda mora em outro sistema e o prontuário em um terceiro, a secretária vira ponte entre três telas.',
      'Falta em consulta custa caro e quase sempre é esquecimento. Confirmação no dia anterior, feita pelo próprio canal em que o paciente já conversa, resolve boa parte disso.',
    ],
    flows: [
      {
        title: 'Agenda de consultas no mesmo lugar da conversa',
        text: 'Serviços, profissionais, unidades e disponibilidade ficam cadastrados uma vez. O horário marcado na conversa aparece na agenda de quem atende.',
        app: 'agenda',
      },
      {
        title: 'Confirmação e lembrete antes da consulta',
        text: 'Você escolhe quando o lembrete dispara e para quem vai: paciente, equipe ou os dois.',
        app: 'reminders',
      },
      {
        title: 'Recepção, fila, prontuário e receita',
        text: 'O CoreMed junta recepção, fila, prontuário eletrônico, receitas, guias de convênio e o financeiro do atendimento. Agenda e Financeiro vêm inclusos, com usuários ilimitados.',
        app: 'clinica',
      },
      {
        title: 'O sistema fala a língua da clínica',
        text: 'Menus e campos passam a dizer paciente, consulta e procedimento, em vez de cliente e pedido.',
        app: 'semantic-clinic',
      },
      {
        title: 'Cobrança e nota fiscal no fechamento',
        text: 'Pix gerado durante o atendimento e NFS-e emitida a partir dele, com o PDF pronto para mandar ao paciente.',
        app: 'payments',
      },
      {
        title: 'Pesquisa depois da consulta, antes da reclamação pública',
        text: 'Passada a consulta, o paciente recebe uma pesquisa curta pelo WhatsApp. O Essencial traz as pesquisas e os painéis. No Completo, nota ruim abre um caso com prazo para a recepção resolver, quem ficou satisfeito recebe o link para avaliar a clínica no Google e a pesquisa pode abrir como formulário dentro do WhatsApp.',
        app: 'customer-success',
      },
    ],
    apps: ['clinica', 'agenda', 'reminders', 'semantic-clinic', 'payments', 'fiscal-nfse', 'customer-success'],
    faq: [
      {
        question: 'A Operax serve para clínica pequena, com um ou dois profissionais?',
        answer:
          'Serve. O plano de entrada já traz atendimento e funil no WhatsApp oficial. A Agenda entra como app, e o CoreMed fica para quando a clínica quer prontuário, receita e guia de convênio no mesmo sistema.',
      },
      {
        question: 'O paciente consegue marcar consulta direto pelo WhatsApp?',
        answer:
          'Consegue. O horário combinado na conversa vira agendamento na agenda do profissional, e o lembrete de confirmação pode sair automaticamente antes da consulta.',
      },
      {
        question: 'Os dados de saúde ficam protegidos?',
        answer:
          'Cada empresa fica isolada no banco, com permissão por perfil e registro das ações sensíveis. O CoreMed foi desenhado seguindo a norma NGS1 (SBIS/CFM) para registro eletrônico em saúde.',
      },
    ],
  },
  {
    slug: 'oficinas-e-autopecas',
    name: 'Oficinas e autopeças',
    searchTerm: 'Sistema para oficina mecânica com WhatsApp',
    headline: 'Oficina com orçamento, ordem de serviço e peça no estoque, pelo WhatsApp.',
    lead: 'O cliente manda a foto do problema, recebe o orçamento, aprova na conversa e acompanha o serviço. A peça sai do estoque e o Pix fecha a conta.',
    context: [
      'Oficina vive de orçamento aprovado. O cliente quer saber quanto vai custar e quando fica pronto, e pergunta isso pelo WhatsApp várias vezes ao dia. Sem histórico por veículo, cada conversa começa do zero.',
      'Autopeças tem outro aperto: o mesmo item vendido no balcão, no WhatsApp e em marketplace. Estoque que não conversa entre os canais vira venda de peça que não existe.',
    ],
    flows: [
      {
        title: 'Histórico por veículo',
        text: 'Cada veículo fica ligado ao cliente, com os serviços já feitos. Quem atende vê o carro antes de responder.',
        app: 'workshop',
      },
      {
        title: 'Ordem de serviço do pedido à entrega',
        text: 'Responsável, prazo, checklist, fotos de evidência e histórico em cada ordem.',
        app: 'service-orders',
      },
      {
        title: 'Estoque de peças que baixa sozinho',
        text: 'Saldo por local, mínimo e máximo, lote e número de série. Venda concluída baixa o estoque.',
        app: 'inventory-catalog',
      },
      {
        title: 'Venda no balcão',
        text: 'PDV no navegador com leitor de código de barras e comprovante impresso, sem instalar programa.',
        app: 'pos',
      },
      {
        title: 'Marketplaces num lugar só',
        text: 'Os pedidos de cada marketplace caem numa lista única, e uma venda em qualquer canal atualiza o saldo dos outros.',
        app: 'commercial-hub',
      },
    ],
    apps: ['workshop', 'semantic-workshop', 'service-orders', 'inventory-catalog', 'pos', 'commercial-hub', 'payments', 'fiscal-nfse'],
    faq: [
      {
        question: 'Consigo mandar orçamento e receber aprovação pelo WhatsApp?',
        answer:
          'Sim. O orçamento é enviado na própria conversa, e a ordem de serviço acompanha o andamento até a entrega do veículo.',
      },
      {
        question: 'O estoque da loja e do marketplace ficam juntos?',
        answer:
          'Ficam. Com o Estoque e o Hub Comercial, o saldo controlado na Operax é publicado em cada canal, e a venda em qualquer um deles atualiza os outros.',
      },
      {
        question: 'Dá para trazer os dados do sistema que uso hoje?',
        answer:
          'O app Trazer dados do sistema antigo lê a pasta do programa atual e traz clientes, produtos, estoque, vendas e o fiado. Você confere tudo antes de importar.',
      },
    ],
  },
  {
    slug: 'restaurantes-e-delivery',
    name: 'Restaurantes e delivery',
    searchTerm: 'Cardápio digital e pedido pelo WhatsApp',
    headline: 'Pedido pelo WhatsApp que chega na cozinha sem ninguém redigitar.',
    lead: 'Cardápio com link próprio, carrinho, Pix e tela de cozinha. O pedido que o cliente monta cai direto no KDS.',
    context: [
      'No horário de pico, restaurante que recebe pedido por mensagem precisa de alguém só para copiar texto para a comanda. É onde o pedido sai errado e o cliente espera.',
      'Cardápio em PDF também não ajuda: o cliente pergunta o preço de novo, pede para trocar item e o atendente responde tudo à mão.',
    ],
    flows: [
      {
        title: 'Cardápio digital com carrinho e Pix',
        text: 'Link próprio em cardapio.operax.digital, com foto, preço, categoria e pagamento por Pix.',
        app: 'cardapio',
      },
      {
        title: 'Cozinha com KDS e comanda',
        text: 'O pedido aparece na tela da cozinha e na comanda, sem redigitação.',
        app: 'cardapio',
      },
      {
        title: 'Catálogo dentro do WhatsApp',
        text: 'Produtos no catálogo do WhatsApp, e o carrinho que o cliente monta chega na conversa.',
        app: 'whatsapp-catalog',
      },
      {
        title: 'Senha e painel no salão',
        text: 'Senha emitida, chamada no guichê e painel na TV, com acompanhamento pelo celular.',
        app: 'queue-panels',
      },
    ],
    apps: ['cardapio', 'whatsapp-catalog', 'queue-panels', 'payments', 'pos', 'linktree'],
    faq: [
      {
        question: 'Preciso de site próprio para ter cardápio digital?',
        answer:
          'Não. O Cardápio digital já vem com link em cardapio.operax.digital, pronto para colocar no Instagram e mandar no WhatsApp.',
      },
      {
        question: 'O cliente paga na hora do pedido?',
        answer: 'Pode pagar por Pix no próprio carrinho, e o pedido segue para a cozinha.',
      },
    ],
  },
  {
    slug: 'varejo-e-comercio',
    name: 'Varejo e comércio',
    searchTerm: 'Sistema de vendas com WhatsApp para loja',
    headline: 'Loja que vende no balcão, no WhatsApp e no marketplace com um estoque só.',
    lead: 'PDV no navegador, catálogo no WhatsApp, pedidos de marketplace numa lista e nota fiscal a partir da venda.',
    context: [
      'Comércio pequeno costuma vender em três lugares ao mesmo tempo e controlar estoque em nenhum deles. O resultado aparece no fim do mês, quando o saldo não bate.',
      'O WhatsApp é onde o cliente pergunta se tem, quanto custa e se entrega. Responder isso consultando outro sistema atrasa a venda.',
    ],
    flows: [
      {
        title: 'PDV no navegador',
        text: 'Leitor de código de barras, venda por peso, escolha do vendedor e comprovante impresso.',
        app: 'pos',
      },
      {
        title: 'Estoque com baixa automática',
        text: 'Saldo por local, lote, validade e número de série, atualizado a cada venda concluída.',
        app: 'inventory-catalog',
      },
      {
        title: 'Catálogo no WhatsApp',
        text: 'Produtos no catálogo do WhatsApp e carrinho recebido na conversa.',
        app: 'whatsapp-catalog',
      },
      {
        title: 'Pedidos de marketplace',
        text: 'Todos os pedidos numa lista, com o estoque da Operax publicado em cada canal.',
        app: 'commercial-hub',
      },
      {
        title: 'Nota fiscal e Pix',
        text: 'NF-e de produto e NFS-e de serviço emitidas a partir do atendimento, e Pix gerado na conversa.',
        app: 'fiscal-nfse',
      },
    ],
    apps: ['pos', 'inventory-catalog', 'whatsapp-catalog', 'commercial-hub', 'fiscal-nfse', 'payments', 'finance', 'migration'],
    faq: [
      {
        question: 'O PDV funciona sem o app de Estoque?',
        answer:
          'Funciona. O PDV vende sozinho. Com o Estoque ativo, cada venda concluída passa a baixar o saldo.',
      },
      {
        question: 'Vendo em marketplace. A Operax evita vender o que não tenho?',
        answer:
          'Com o Hub Comercial, uma venda em qualquer canal atualiza o saldo publicado nos outros na hora.',
      },
    ],
  },
  {
    slug: 'servicos',
    name: 'Serviços e atendimento em campo',
    searchTerm: 'Sistema de ordem de serviço com WhatsApp',
    headline: 'Empresa de serviço com chamado, técnico e contrato no mesmo sistema.',
    lead: 'O cliente abre o chamado pelo WhatsApp, acompanha pelo portal, assina o contrato por link e responde a pesquisa de satisfação no final.',
    context: [
      'Em serviço, a pergunta que mais chega é "e aí, como está?". Sem ordem de serviço com status, cada resposta depende de ligar para o técnico.',
      'Contrato impresso, assinado e escaneado atrasa o início do trabalho e some na pasta.',
    ],
    flows: [
      {
        title: 'Ordem de serviço com evidência',
        text: 'Responsável, prazo, checklist, fotos e histórico do pedido até a conclusão.',
        app: 'service-orders',
      },
      {
        title: 'Portal do cliente',
        text: 'O cliente entra por QR, e-mail ou WhatsApp e vê status, agenda, documentos e aprovações.',
        app: 'client-portal',
      },
      {
        title: 'Contrato assinado por link',
        text: 'Contrato gerado de modelo com variáveis, assinatura eletrônica por link e pré-assinatura com certificado ICP-Brasil A1 da empresa.',
        app: 'contracts',
      },
      {
        title: 'Pesquisa de satisfação no WhatsApp',
        text: 'NPS, CSAT e CES enviados na conversa, com recuperação de quem avaliou mal.',
        app: 'customer-success',
      },
    ],
    apps: ['service-orders', 'client-portal', 'contracts', 'customer-success', 'agenda', 'finance', 'payments', 'fiscal-nfse'],
    faq: [
      {
        question: 'O cliente consegue acompanhar o serviço sem me chamar?',
        answer:
          'Consegue, pelo Portal do cliente. Ele vê o status do serviço, documentos e o que falta aprovar.',
      },
      {
        question: 'A assinatura eletrônica do contrato tem validade?',
        answer:
          'O app Contratos colhe assinatura eletrônica por link público e aceita pré-assinatura com o certificado ICP-Brasil A1 da empresa.',
      },
    ],
  },
  {
    slug: 'beleza-e-estetica',
    name: 'Beleza e estética',
    searchTerm: 'Agendamento pelo WhatsApp para salão e estética',
    headline: 'Salão e estética com horário marcado pelo WhatsApp e menos falta.',
    lead: 'Agenda por profissional, lembrete antes do horário, Pix no fechamento e pesquisa depois do atendimento.',
    context: [
      'Salão perde dinheiro com cadeira vazia. A cliente marca, esquece e o horário fica parado.',
      'Quando a agenda está num caderno e as conversas no celular de cada profissional, ninguém sabe quem confirmou.',
    ],
    flows: [
      {
        title: 'Agenda por profissional',
        text: 'Serviços com duração, profissionais e horários de cada um, no mesmo lugar da conversa.',
        app: 'agenda',
      },
      {
        title: 'Lembrete antes do horário',
        text: 'Lembrete no contato ou no card, disparado quando você escolher.',
        app: 'reminders',
      },
      {
        title: 'Pix no fim do atendimento',
        text: 'Cobrança Pix gerada na conversa, com acompanhamento do recebimento.',
        app: 'payments',
      },
      {
        title: 'Link na bio',
        text: 'Página de links para o Instagram, com WhatsApp, endereço e formulário que cria lead no CRM.',
        app: 'linktree',
      },
    ],
    apps: ['agenda', 'reminders', 'payments', 'linktree', 'customer-success'],
    faq: [
      {
        question: 'Consigo mandar lembrete automático para a cliente?',
        answer:
          'Sim. O app Lembretes dispara no momento que você escolher e pode avisar a cliente, a equipe ou os dois.',
      },
    ],
  },
  {
    slug: 'eventos-e-cursos',
    name: 'Eventos e cursos',
    searchTerm: 'Inscrição em evento pelo WhatsApp',
    headline: 'Inscrição em evento e curso feita pelo WhatsApp ou por link.',
    lead: 'Formulário personalizado, inscrição pelo WhatsApp ou link público, cobrança por Pix e relatório exportável.',
    context: [
      'Evento pequeno costuma juntar inscrição por formulário, pagamento por transferência e lista em planilha. Conferir quem pagou vira trabalho de véspera.',
    ],
    flows: [
      {
        title: 'Inscrição pelo WhatsApp ou link',
        text: 'Formulário personalizado, métricas de inscrição e exportação de relatório.',
        app: 'events',
      },
      {
        title: 'Cobrança por Pix',
        text: 'Pix gerado na conversa, com acompanhamento do recebimento.',
        app: 'payments',
      },
      {
        title: 'Lembrete para o participante',
        text: 'Aviso antes do evento, no momento que você definir.',
        app: 'reminders',
      },
    ],
    apps: ['events', 'payments', 'reminders', 'fiscal-nfse', 'linktree'],
    faq: [
      {
        question: 'Preciso pagar a mais para receber inscrições?',
        answer: 'O app Inscrições em Eventos vem incluso no plano.',
      },
    ],
  },
];

export function findSector(slug: string): Sector | undefined {
  return sectors.find((s) => s.slug === slug);
}
